<template>
  <div class="dropdown-container">
    <!-- Selected Country Button -->
    <button
      class="dropdown-button"
      :style="{ width: showCountryName ? '100%' : 'auto' }"
      type="button"
      @click="isDropdownOpen = !isDropdownOpen"
    >
      <span class="flag" :class="[userSelectedCountry ? `flag:${userSelectedCountry?.code}` : '']"></span>
      <span v-if="showCountryName">{{
        userSelectedCountry ? userSelectedCountry?.name : $t('countrySelector.selectCountry')
      }}</span>
      <span class="caret"></span>
    </button>

    <!-- Dropdown (Desktop) -->
    <ul
      v-if="isDropdownOpen && !isMobile"
      class="dropdown-list"
      :style="{
        width: dropdownWidth,
        left: dropdownLeft + 'px',
        top: dropdownLeft === 0 ? undefined : 2 + 'rem',
      }"
    >
      <li>
        <div class="search-container">
          <div class="search-box">
            <span class="icon">
              <SearchIcon />
            </span>
            <input
              type="text"
              :placeholder="$t('countrySelector.selectCountry')"
              v-model="searchQuery"
              autofocus
              autocomplete="off"
            />
          </div>
        </div>
      </li>
      <li v-for="country in filteredCountries" :key="country.code" @click="selectCountry(country)">
        <span class="flag" :class="[country ? `flag:${country.code}` : '']"></span>
        <span>{{ country.name }} (+{{ country.prefix }})</span>
      </li>
    </ul>

    <!-- Fullscreen Modal (Mobile) -->
    <div class="mobile-dropdown" :class="{ show: isDropdownOpen && isMobile }">
      <div class="search-header">
        <h2>{{ $t('countrySelector.selectCountry') }}</h2>
        <button
          type="button"
          aria-label="back-sheet"
          class="close-button"
          @click="
            isDropdownOpen = false;
            searchQuery = '';
          "
        >
          <CloseIcon />
        </button>
      </div>
      <div class="search-container">
        <div class="search-box">
          <span class="icon">
            <SearchIcon />
          </span>
          <input
            type="text"
            :placeholder="$t('countrySelector.selectCountry')"
            v-model="searchQuery"
            autofocus
            autocomplete="off"
          />
        </div>
      </div>
      <div class="mobile-country-list-wrapper">
        <ul>
          <li v-for="country in filteredCountries" :key="country.code" @click="selectCountry(country)">
            <span class="flag" :class="[country ? `flag:${country.code}` : '']"></span>
            <span>{{ country.name }} (+{{ country.prefix }})</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { NamedCountry } from '@/models/namedcountry';
import { default as _countries } from '@/data/countries.json';
import CloseIcon from '@/components/icons/CloseIcon.vue';
import SearchIcon from '@/components/icons/SearchIcon.vue';
import { ref, computed, defineComponent, onMounted, onUnmounted, onBeforeUnmount, PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import { Country } from '@/models/country';

export default defineComponent({
  name: 'CountrySelector',
  components: { SearchIcon, CloseIcon },
  props: {
    defaultCountries: {
      type: Array as PropType<Country[]>,
      required: false,
      default: () => [],
    },
    selectedCountry: {
      type: [String, Object] as PropType<string | NamedCountry>,
      required: false,
      default: '',
    },
    showCountryName: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  emits: ['update:selectedCountry'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const isDropdownOpen = ref(false);
    const isMobile = ref(window.innerWidth < 768);
    const searchQuery = ref('');
    const dropdownWidth = ref('0px');
    const dropdownLeft = ref(0);

    let observer: ResizeObserver;

    const countries = computed(() => {
      if (props.defaultCountries.length) {
        return props.defaultCountries.map((country) => ({
          ...country,
          name: t(`countries.${country.code.toUpperCase()}`),
        }));
      }
      return _countries.map((country) => ({
        ...country,
        name: t(`countries.${country.code.toUpperCase()}`),
      }));
    });

    const filteredCountries = computed(() => {
      const result = countries.value.filter((c) => c.name.toLowerCase().includes(searchQuery.value.toLowerCase()));
      return result.length ? result : countries.value;
    });

    const userSelectedCountry = computed<NamedCountry | null>(() => {
      if (!props.selectedCountry) return null;
      const country =
        typeof props.selectedCountry === 'object' && 'code' in props.selectedCountry
          ? props.selectedCountry.code
          : props.selectedCountry;
      const countryFound =
        countries.value.find((_country) => _country.code.toLowerCase() === country.toLowerCase()) || null;
      emit('update:selectedCountry', countryFound);

      return countryFound;
    });

    const updateIsMobile = () => {
      isMobile.value = window.innerWidth < 768;
    };

    const handleClickOutside = (event: Event) => {
      if (!(event.target as HTMLElement).closest('.dropdown-container')) {
        isDropdownOpen.value = false;
        searchQuery.value = '';
      }
    };

    const selectCountry = (country: NamedCountry) => {
      isDropdownOpen.value = false;
      searchQuery.value = '';
      emit('update:selectedCountry', country);
    };

    onMounted(() => {
      window.addEventListener('resize', updateIsMobile);
      document.addEventListener('click', handleClickOutside);
      const el = document.querySelector('.phone-number-selector-container');
      const dropdownButton = document.querySelector('.dropdown-button');

      if (el) {
        dropdownLeft.value = -12;
        observer = new ResizeObserver((entries) => {
          for (const entry of entries) {
            dropdownWidth.value = entry.contentRect.width + 'px';
          }
        });
        observer.observe(el);
      } else {
        if (dropdownButton) {
          dropdownWidth.value = '100%';
          dropdownLeft.value = 0;
        }
      }
    });

    onBeforeUnmount(() => {
      if (observer) observer.disconnect();
    });

    onUnmounted(() => {
      window.removeEventListener('resize', updateIsMobile);
      document.removeEventListener('click', handleClickOutside);
    });

    return {
      isDropdownOpen,
      isMobile,
      userSelectedCountry,
      searchQuery,
      filteredCountries,
      dropdownWidth,
      dropdownLeft,
      selectCountry,
    };
  },
});
</script>

<style scoped lang="scss">
@import '../flags';

.dropdown-container {
  position: relative;
  display: inline-block;
}

.dropdown-button {
  padding: 0.75rem;
  font-size: 1rem;
  background: $white;
  border: 0.75px solid $color-ube;
  color: $color-space-blue;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .flag {
    font-size: 20px;
  }

  .caret {
    appearance: none;
    background-image: url('../assets/chevron-down.svg');
    background-repeat: no-repeat;
    background-position: right;
    background-color: white;
    margin-left: auto;
    width: 1rem;
    height: 1rem;
  }
}

/* Dropdown List (Desktop) */
.dropdown-list {
  position: absolute;
  max-height: 300px;
  overflow-y: scroll;
  background: $white;
  border: 0.75px solid $color-ube;
  border-radius: 8px;
  list-style: none;
  padding: 0;
  margin: 5px 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 10;

  .search-container {
    margin-bottom: 0;
  }

  li {
    padding: 1rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    box-sizing: border-box;

    &:first-of-type {
      padding-bottom: 0;
      width: 100%;
    }

    &:not(:first-of-type) {
      &:hover {
        background: lighten($primary-color, 40%);
      }
    }
  }
}

.search-header {
  display: flex;
  padding-left: 0.2rem;
  padding-right: 0.2rem;
  justify-content: space-between;
  align-items: center;

  h2 {
    color: $color-space-blue;
    font-size: 1.25rem;
    font-weight: 600;
    margin-top: 1rem;
    margin-bottom: 1rem;
  }
}

.close-button {
  margin-left: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 1.75rem;
  height: 1.75rem;
  background-color: #e0ecf1;
  border-radius: 9999px;
  border: none;
}

.search-container {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  width: 100%;
}

.search-box {
  display: flex;
  align-items: center;
  flex-grow: 1;
  background: white;
  border: 1px solid $color-bright-grey;
  border-radius: 999px;

  .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    width: 40px;
    border-radius: 8px;
  }

  input {
    flex-grow: 1;
    border: none;
    background: transparent;
    padding: 12px 10px;
    padding-left: 0;
    font-size: 16px;
    color: $color-space-blue;
    outline: none !important;
    &:not(.blank-styling) {
      border: none !important;
    }
  }

  input:focus {
    border: none;
    outline: none;
  }
}

/* Mobile Fullscreen Drop-up */
.mobile-dropdown {
  position: fixed;
  bottom: -100%;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  z-index: 1000;
  background: $white;
  padding: 10px;
  transition: bottom 200ms cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;

  &.show {
    bottom: 0%;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    overflow-y: auto;

    li {
      padding: 1.25rem 1rem;
      display: flex;
      align-items: center;
      gap: 10px;
      cursor: pointer;

      &:hover {
        background: lighten($primary-color, 40%);
      }
    }
  }

  .mobile-country-list-wrapper {
    overflow-y: auto;
    border-radius: 12px 12px 0 0;
    background-color: white;
    height: calc(100% - 56px);
  }
}

.text-header {
  margin-top: 1.25rem;
  margin-bottom: 0.5rem;
  font-size: 1.25rem;
  line-height: 1.75rem;
}
</style>
